import { useEffect, useState } from 'react';
import { Button, StatusBadge } from '@/components/common';
import { get } from '@/api/client';
import { createTcSheet, deleteTcSheet, listTcSheets, upgradeTcSheets, type TcManagedSheet } from '@/api/tc-sheets';
import type { Team } from '@/types/api';

interface SheetsSettingsProps {
  team: Team | null;
}

const FORMULAS = [
  '=TC("Shopify","Revenue",DATE(2026,6,1))',
  '=TC("Shopify","Revenue",DATE(2026,6,1),DATE(2026,6,10),TRUE)',
  '=TC("Shopify","Orders",DATE(2026,6,1))',
  '=TC("Shopify","Orders",DATE(2026,6,1),DATE(2026,6,10),TRUE)',
  '=TC("ShipBob","TCT4000",TRUE)',
  '=TC("ShipBob","TCT4000")',
  '=TC("ShipBob","All SKUs",TRUE)',
  '=TC("Klaviyo","Campaigns","All Metrics",30,TRUE)',
  '=TC("Okendo","All Products",30,TRUE)',
  '=TC("TripleWhale","Meta",30,TRUE)',
  '=TC("Gorgias","All Metrics",30,TRUE)',
];

type TcSheetError = {
  message: string;
  actionUrl?: string;
};

function parseTcSheetError(error: unknown, fallback: string): TcSheetError {
  const detail = (error as { detail?: unknown }).detail;
  if (!detail) return { message: fallback };

  if (typeof detail === 'object') {
    const record = detail as { message?: unknown; enable_url?: unknown };
    return {
      message: typeof record.message === 'string' ? record.message : fallback,
      actionUrl: typeof record.enable_url === 'string' ? record.enable_url : undefined,
    };
  }

  if (typeof detail === 'string') {
    try {
      const parsed = JSON.parse(detail) as { message?: unknown; enable_url?: unknown };
      return {
        message: typeof parsed.message === 'string' ? parsed.message : detail,
        actionUrl: typeof parsed.enable_url === 'string' ? parsed.enable_url : undefined,
      };
    } catch {
      return { message: detail || fallback };
    }
  }

  return { message: fallback };
}

function tcSheetStatusTone(status: string): 'neutral' | 'success' | 'warning' | 'danger' {
  if (status === 'active') return 'success';
  if (status === 'failed') return 'danger';
  if (status === 'provisioning') return 'warning';
  return 'neutral';
}

function Icon({
  name,
  size = 16,
}: {
  name: 'sheet' | 'external' | 'plus' | 'refresh' | 'copy' | 'trash';
  size?: number;
}) {
  const paths = {
    sheet: <><path d="M7 3h7l4 4v14H7z" /><path d="M14 3v5h5" /><path d="M10 13h6M10 17h6" /></>,
    external: <><path d="M14 4h6v6" /><path d="M20 4l-9 9" /><path d="M20 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h5" /></>,
    plus: <><path d="M12 5v14" /><path d="M5 12h14" /></>,
    refresh: <><path d="M20 12a8 8 0 0 1-13.7 5.6" /><path d="M4 18v-5h5" /><path d="M4 12A8 8 0 0 1 17.7 6.4" /><path d="M20 6v5h-5" /></>,
    copy: <><path d="M9 9h10v10H9z" /><path d="M5 15H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v1" /></>,
    trash: <><path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v5" /><path d="M14 11v5" /></>,
  };
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {paths[name]}
    </svg>
  );
}

export function SheetsSettings({ team }: SheetsSettingsProps) {
  const [sheets, setSheets] = useState<TcManagedSheet[]>([]);
  const [missingScopes, setMissingScopes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [deletingSheetId, setDeletingSheetId] = useState('');
  const [notice, setNotice] = useState('');
  const [actionUrl, setActionUrl] = useState('');

  const loadSheets = async (options?: { preserveAction?: boolean }) => {
    if (!team?.id) return;
    setIsLoading(true);
    if (!options?.preserveAction) {
      setActionUrl('');
    }
    try {
      const result = await listTcSheets(team.id);
      setSheets(result.sheets);
      setMissingScopes(result.missing_scopes || []);
      if (result.upgraded?.length) {
        setNotice(`Updated ${result.upgraded.length} managed Sheet runtime${result.upgraded.length === 1 ? '' : 's'}. Reopen the Sheet to load the latest True Classic script.`);
      }
    } catch (error) {
      const parsed = parseTcSheetError(error, 'Could not load managed Sheets.');
      setNotice(parsed.message);
      setActionUrl(parsed.actionUrl || '');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadSheets();
  }, [team?.id]);

  const handleConnectGoogle = async () => {
    const data = await get<{ url: string }>('/api/user/google-workspace/connect', {
      redirect_to: window.location.origin,
    });
    window.location.href = data.url;
  };

  const handleCreate = async () => {
    if (!team?.id || isCreating) return;
    setIsCreating(true);
    setNotice('');
    setActionUrl('');
    try {
      const result = await createTcSheet(team.id);
      setSheets((current) => [result.sheet, ...current]);
      window.open(result.sheet.spreadsheet_url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      const parsed = parseTcSheetError(error, 'Could not create the Sheet.');
      setNotice(parsed.message);
      setActionUrl(parsed.actionUrl || '');
      await loadSheets({ preserveAction: true });
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpgradeRuntime = async () => {
    if (!team?.id || isUpgrading) return;
    setIsUpgrading(true);
    setNotice('');
    setActionUrl('');
    try {
      const result = await upgradeTcSheets(team.id);
      setSheets(result.sheets);
      setNotice(`Updated ${result.upgraded.length} managed Sheet runtime${result.upgraded.length === 1 ? '' : 's'}. Reopen the Sheet to load the latest True Classic script.`);
    } catch (error) {
      const parsed = parseTcSheetError(error, 'Could not update the Sheet runtime.');
      setNotice(parsed.message);
      setActionUrl(parsed.actionUrl || '');
    } finally {
      setIsUpgrading(false);
    }
  };

  const handleDeleteSheet = async (sheet: TcManagedSheet) => {
    if (!team?.id || deletingSheetId) return;
    if (sheet.status === 'active') {
      const confirmed = window.confirm(`Remove "${sheet.title}" from managed Sheets? The Google Sheet file will not be deleted from Drive.`);
      if (!confirmed) return;
    }
    setDeletingSheetId(sheet.id);
    setNotice('');
    setActionUrl('');
    try {
      const result = await deleteTcSheet(team.id, sheet.id);
      setSheets(result.sheets);
      setNotice(`Deleted ${sheet.title} from managed Sheets.`);
    } catch (error) {
      const parsed = parseTcSheetError(error, 'Could not delete the managed Sheet.');
      setNotice(parsed.message);
      setActionUrl(parsed.actionUrl || '');
    } finally {
      setDeletingSheetId('');
    }
  };

  const copyFormula = async (formula: string) => {
    await navigator.clipboard.writeText(formula);
    setNotice('Formula copied.');
  };

  if (!team) {
    return (
      <div id="sheetsSettingsPanel" className="settings-tab-panel">
        <div className="settings-empty-state">
          <h3>No Team Selected</h3>
          <p>Select the True Classic team before creating managed Sheets.</p>
        </div>
      </div>
    );
  }

  const needsReconnect = missingScopes.length > 0;

  return (
    <div id="sheetsSettingsPanel" className="settings-tab-panel">
      <div className="settings-section">
        <div className="section-header">
          <h3 className="section-header__title">True Classic Sheets</h3>
          <p className="section-header__description">
            Create managed Google Sheets with the True Classic formula runtime installed.
          </p>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
          <StatusBadge
            label={needsReconnect ? 'Google reconnect required' : 'Ready'}
            tone={needsReconnect ? 'warning' : 'success'}
            size="sm"
          />
          <Button variant="outline" onClick={() => loadSheets()} isLoading={isLoading} leftIcon={<Icon name="refresh" />}>
            Refresh
          </Button>
          {!needsReconnect && sheets.length > 0 && (
            <Button variant="outline" onClick={handleUpgradeRuntime} isLoading={isUpgrading} leftIcon={<Icon name="refresh" />}>
              Update runtime
            </Button>
          )}
          {needsReconnect ? (
            <Button onClick={handleConnectGoogle}>Connect Google Workspace</Button>
          ) : (
            <Button onClick={handleCreate} isLoading={isCreating} leftIcon={<Icon name="plus" />}>
              Create Sheet
            </Button>
          )}
        </div>

        {notice && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: 0 }}>{notice}</p>
            {actionUrl && (
              <Button
                variant="outline"
                onClick={() => window.open(actionUrl, '_blank', 'noopener,noreferrer')}
                leftIcon={<Icon name="external" />}
              >
                Open Google API Console
              </Button>
            )}
          </div>
        )}

        <div style={{ display: 'grid', gap: 12 }}>
          {sheets.map((sheet) => (
            <div
              key={sheet.id}
              style={{
                border: '1px solid var(--border-color)',
                borderRadius: 8,
                padding: 14,
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                alignItems: 'center',
              }}
            >
              <div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 600 }}>
                  <Icon name="sheet" size={18} />
                  {sheet.title}
                  <StatusBadge label={sheet.status} tone={tcSheetStatusTone(sheet.status)} size="sm" />
                </div>
                <p style={{ margin: '4px 0 0', color: 'var(--text-secondary)', fontSize: 13 }}>
                  {sheet.template_version} · {sheet.last_synced_at ? `Synced ${new Date(sheet.last_synced_at).toLocaleString()}` : 'Not synced yet'}
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <Button
                  variant="outline"
                  onClick={() => window.open(sheet.spreadsheet_url, '_blank', 'noopener,noreferrer')}
                  leftIcon={<Icon name="external" />}
                >
                  Open
                </Button>
                <Button
                  variant="danger"
                  onClick={() => handleDeleteSheet(sheet)}
                  isLoading={deletingSheetId === sheet.id}
                  leftIcon={<Icon name="trash" />}
                >
                  Delete
                </Button>
              </div>
            </div>
          ))}

          {!sheets.length && !isLoading && (
            <div className="settings-empty-state">
              <h3>No managed Sheets yet</h3>
              <p>Create one to install the True Classic menu and TC formula runtime.</p>
            </div>
          )}
        </div>
      </div>

      <div className="settings-section">
        <h3 className="section-title">Formula Examples</h3>
        <div style={{ display: 'grid', gap: 10 }}>
          {FORMULAS.map((formula) => (
            <button
              key={formula}
              type="button"
              onClick={() => copyFormula(formula)}
              style={{
                border: '1px solid var(--border-color)',
                borderRadius: 8,
                padding: '10px 12px',
                background: 'var(--surface-color)',
                color: 'var(--text-primary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <code style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{formula}</code>
              <Icon name="copy" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default SheetsSettings;
